import type { Person, RuleMode, WritingRules } from "@storyai/shared";

const MODES: [RuleMode, string][] = [
  ["off", "Off"],
  ["feedback", "Feedback"],
  ["required", "Required"],
];

const SIMPLE: [Exclude<keyof WritingRules, "person" | "minWords">, string, string][] = [
  ["capitalization", "Capital letters", "Sentence starts, “I”, names."],
  ["punctuation", "Punctuation", "End marks, quotes, commas."],
  ["completeSentences", "Complete sentences", "No fragments."],
  ["noTextSpeak", "No text-speak", "No “u”, “lol”, “gonna”."],
  ["descriptive", "Descriptive language", "At least one adjective or adverb that adds detail."],
  ["spelling", "Spelling", "Misspelled words; names from the story are exempt."],
];

/**
 * One row per writing rule with its mode. "Feedback" = the coach comments and
 * awards points; "Required" = the message bounces back until it's fixed.
 */
export function WritingRulesEditor({ value, onChange }: { value: WritingRules; onChange: (r: WritingRules) => void }) {
  const setMode = (key: keyof WritingRules, mode: RuleMode) => {
    if (key === "person") onChange({ ...value, person: { ...value.person, mode } });
    else if (key === "minWords") onChange({ ...value, minWords: { ...value.minWords, mode } });
    else onChange({ ...value, [key]: mode });
  };

  const modeSelect = (key: keyof WritingRules, mode: RuleMode) => (
    <select value={mode} onChange={(e) => setMode(key, e.target.value as RuleMode)} aria-label={`${key} mode`}>
      {MODES.map(([m, label]) => (
        <option key={m} value={m}>
          {label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="rules-editor">
      <div className="rules-legend muted small">
        Off · Feedback (the coach comments, points for meeting it) · Required (must be fixed before the story continues)
      </div>
      {SIMPLE.map(([key, label, hint]) => (
        <div key={key} className="rule-row">
          <div className="rule-main">
            <span className="rule-label">{label}</span>
            <span className="muted small"> {hint}</span>
          </div>
          {modeSelect(key, value[key])}
        </div>
      ))}
      <div className="rule-row">
        <div className="rule-main">
          <span className="rule-label">Point of view</span>
          <select
            className="rule-sub"
            value={value.person.person}
            onChange={(e) => onChange({ ...value, person: { ...value.person, person: Number(e.target.value) as Person } })}
            aria-label="Required person"
            disabled={value.person.mode === "off"}
          >
            <option value={1}>1st person (I, we)</option>
            <option value={2}>2nd person (you)</option>
            <option value={3}>3rd person (he, she, they)</option>
          </select>
        </div>
        {modeSelect("person", value.person.mode)}
      </div>
      <div className="rule-row">
        <div className="rule-main">
          <span className="rule-label">Minimum length</span>
          <input
            type="number"
            className="rule-sub rule-num"
            min={1}
            max={200}
            value={value.minWords.count}
            onChange={(e) =>
              onChange({ ...value, minWords: { ...value.minWords, count: Math.max(1, Math.min(200, Number(e.target.value) || 1)) } })
            }
            aria-label="Minimum words"
            disabled={value.minWords.mode === "off"}
          />
          <span className="muted small"> words</span>
        </div>
        {modeSelect("minWords", value.minWords.mode)}
      </div>
    </div>
  );
}
